import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, Printer, Columns3 } from "lucide-react";

export type ListagemColumn<Row> = {
  key: string;
  label: string;
  /** Value used for export/print. */
  value: (row: Row) => unknown;
  /** If false, it won't appear in the column selector and defaults to always-hidden there. */
  toggleable?: boolean;
};

function toCsvValue(value: unknown) {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildCsv<Row>(rows: Row[], columns: ListagemColumn<Row>[]) {
  if (!rows.length) return "";
  const headers = columns.map((c) => c.label);
  const content = [
    headers.map(toCsvValue).join(","),
    ...rows.map((r) => columns.map((c) => toCsvValue(c.value(r))).join(",")),
  ].join("\n");
  return content;
}

function downloadText(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadBytes(filename: string, bytes: ArrayBuffer | Uint8Array, mime: string) {
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCellValue(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  return String(value);
}

function escapeHtml(s: string) {
  return s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function buildPrintHtml<Row>(title: string, subtitle: string | undefined, rows: Row[], columns: ListagemColumn<Row>[]) {
  const header = `
    <div class="header">
      <div class="title">${escapeHtml(title)}</div>
      ${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ""}
      <div class="meta">Gerado em ${new Date().toLocaleString("pt-BR")}</div>
    </div>
  `;

  const thead = `<thead><tr>${columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("")}</tr></thead>`;
  const tbody = `<tbody>${rows
    .map((r) => {
      const tds = columns
        .map((c) => {
          const v = c.value(r);
          return `<td>${escapeHtml(v === null || v === undefined ? "" : String(v))}</td>`;
        })
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("")}</tbody>`;

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8"/>
      <title>${escapeHtml(title)}</title>
      <style>
        body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial; padding: 24px; color: #111827; }
        .header { margin-bottom: 16px; }
        .title { font-size: 18px; font-weight: 700; }
        .subtitle { font-size: 12px; color: #4b5563; margin-top: 4px; }
        .meta { font-size: 10px; color: #6b7280; margin-top: 6px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 11px; vertical-align: top; }
        th { background: #f9fafb; text-align: left; font-weight: 600; }
        @media print {
          body { padding: 0; }
          .header { page-break-after: avoid; }
        }
      </style>
    </head>
    <body>
      ${header}
      <table>
        ${thead}
        ${tbody}
      </table>
    </body>
  </html>`;
}

export function ListagemAcoes<Row>({
  title,
  subtitle,
  rows,
  fetchAllRows,
  columns,
  visibleKeys,
  onToggleColumn,
  filenameBase,
  enablePrint = true,
  enableExport = true,
}: {
  title: string;
  subtitle?: string;
  rows: Row[];
  fetchAllRows?: () => Promise<Row[]>;
  columns: ListagemColumn<Row>[];
  visibleKeys: string[];
  onToggleColumn: (key: string, nextVisible: boolean) => void;
  filenameBase: string;
  enablePrint?: boolean;
  enableExport?: boolean;
}) {
  const [isWorking, setIsWorking] = useState(false);
  const toggleableColumns = useMemo(
    () => columns.filter((c) => c.toggleable !== false),
    [columns]
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleKeys.includes(c.key)),
    [columns, visibleKeys]
  );

  const resolveRows = async () => {
    if (fetchAllRows) {
      return await fetchAllRows();
    }
    return rows;
  };

  const handleExportCsv = async () => {
    try {
      setIsWorking(true);
      const allRows = await resolveRows();
      const csv = buildCsv(allRows, visibleColumns);
      if (!csv) return;
      downloadText(`${filenameBase}.csv`, csv, "text/csv;charset=utf-8");
    } finally {
      setIsWorking(false);
    }
  };

  const handleExportXlsx = async () => {
    try {
      setIsWorking(true);
      const allRows = await resolveRows();
      if (!allRows.length) return;

      const XLSX = await import("xlsx");
      const data = allRows.map((r) => {
        const obj: Record<string, unknown> = {};
        for (const c of visibleColumns) {
          obj[c.label] = toCellValue(c.value(r));
        }
        return obj;
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dados");
      const bytes = XLSX.write(wb, { bookType: "xlsx", type: "array" }) as Uint8Array;

      downloadBytes(
        `${filenameBase}.xlsx`,
        bytes,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    } finally {
      setIsWorking(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setIsWorking(true);
      const allRows = await resolveRows();
      if (!allRows.length) return;

      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      doc.setFontSize(12);
      doc.text(title, 40, 30);
      if (subtitle) {
        doc.setFontSize(9);
        doc.text(subtitle, 40, 44);
      }
      doc.setFontSize(8);
      doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, 40, subtitle ? 58 : 44);

      autoTable(doc, {
        startY: subtitle ? 70 : 56,
        head: [visibleColumns.map((c) => c.label)],
        body: allRows.map((r) => visibleColumns.map((c) => String(toCellValue(c.value(r))))),
        styles: { fontSize: 7, cellPadding: 3, overflow: "linebreak" },
        headStyles: { fillColor: [249, 250, 251], textColor: 17 },
        margin: { left: 40, right: 40 },
      });

      const bytes = doc.output("arraybuffer");
      downloadBytes(`${filenameBase}.pdf`, bytes, "application/pdf");
    } finally {
      setIsWorking(false);
    }
  };

  const handlePrint = async () => {
    let objectUrl: string | null = null;
    try {
      setIsWorking(true);
      const allRows = await resolveRows();
      const html = buildPrintHtml(title, subtitle, allRows, visibleColumns);
      // Firefox (e alguns outros) pode deixar a janela em branco com about:blank + document.write
      // quando se usa noopener/noreferrer. Abrir via blob URL é estável para impressão.
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      objectUrl = URL.createObjectURL(blob);
      const w = window.open(objectUrl, "_blank", "width=1024,height=768");
      if (!w) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
        return;
      }
      let printed = false;
      const runPrint = () => {
        if (printed) return;
        if (w.document.readyState !== "complete" && w.document.readyState !== "interactive") return;
        printed = true;
        try {
          w.focus();
          w.print();
        } catch {
          // ignore
        }
      };
      const cleanup = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };
      w.addEventListener(
        "load",
        () => {
          // Pequeno atraso para o layout/paint antes do diálogo de impressão
          requestAnimationFrame(() => {
            setTimeout(runPrint, 100);
          });
        },
        { once: true }
      );
      // Fallback se "load" não disparar como esperado
      setTimeout(runPrint, 500);
      const finish = () => {
        cleanup();
        try {
          w.close();
        } catch {
          // ignore
        }
      };
      w.addEventListener("afterprint", finish, { once: true });
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Columns3 className="h-4 w-4" />
            Colunas
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Exibir colunas</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {toggleableColumns.map((c) => (
            <DropdownMenuCheckboxItem
              key={c.key}
              checked={visibleKeys.includes(c.key)}
              onCheckedChange={(checked) => onToggleColumn(c.key, Boolean(checked))}
            >
              {c.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {enablePrint && (
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          disabled={isWorking || rows.length === 0}
        >
          <Printer className="h-4 w-4" />
          {isWorking ? "Preparando..." : "Imprimir"}
        </Button>
      )}
      {enableExport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={isWorking || rows.length === 0}>
              <Download className="h-4 w-4" />
              {isWorking ? "Preparando..." : "Exportar"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Formato</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleExportCsv}>CSV (.csv)</DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportXlsx}>Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportPdf}>PDF (.pdf)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

